import { FC } from 'react';
import { PATH } from '@/app/routes';
import { useAppSelector } from '@/app/store';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRTL, useScreenWidth } from '@/shared/hooks';
import { LanguageToggle } from './LanguageToggle';
import { CatalogMenuCategories } from './CatalogMenuCategories';
import { ClientMenuCategoryDto } from '@/app/store/api/restApi';
import { Separator, Sheet, SheetContent, SheetTitle } from '@/shared/shadcn';
import { useHandleDocumentNavigate } from '@/features/documents/hooks/useHandleDocumentNavigate';
import { cn, isTelegramWebApp } from '@/shared/lib';

// Tailwind `md`. Below it the menu is a full-screen, non-modal sheet so the catalog header (logo,
// profile, cart) stays interactive on top; at `md`+ it becomes a modal side panel.
const MD_BREAKPOINT = 768;

interface CatalogMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories?: ClientMenuCategoryDto[];
}

export const CatalogMenu: FC<CatalogMenuProps> = ({ open, onOpenChange, categories }) => {
  const isRTL = useRTL();
  const nav = useNavigate();
  const { width } = useScreenWidth();
  const isMdUp = width >= MD_BREAKPOINT;
  const isTgWebApp = isTelegramWebApp();
  const { t: tAbout } = useTranslation('aboutRestaurant');
  const { t: tDocuments } = useTranslation('documents');
  const { handleNavigate } = useHandleDocumentNavigate();
  const { hasLegalInfo } = useAppSelector((state) => state.application.demoMode);
  const legalDocuments = useAppSelector((state) => state.documents.legalDocuments);

  const close = () => onOpenChange(false);

  const goToAbout = () => {
    nav(PATH.aboutPage);
    close();
  };

  const legalLinks = [
    { label: tDocuments('sections.privacyPolicy'), document: legalDocuments?.privacyPolicy },
    { label: tDocuments('sections.consentPD'), document: legalDocuments?.consentToPersonalDataProcessing },
    { label: tDocuments('sections.termsOfUse'), document: legalDocuments?.termsOfUse },
  ].filter((link) => Boolean(link.document));

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={isMdUp}>
      <SheetContent
        side={isRTL ? 'right' : 'left'}
        dir={isRTL ? 'rtl' : 'ltr'}
        showClose={isMdUp}
        aria-describedby={undefined}
        className={cn(
          'gap-0 px-4 pt-[90px] pb-4 md:px-8 md:pb-8 max-md:max-w-full max-md:pt-[var(--height-header)]',
          isTgWebApp && 'pb-8',
        )}
      >
        <SheetTitle className='sr-only'>Menu</SheetTitle>

        {categories && (
          <>
            <CatalogMenuCategories categories={categories} onNavigate={close} />
            <Separator className='my-4' />
          </>
        )}

        <div className='flex flex-col justify-between flex-1 gap-4'>
          <div>
            <button
              type='button'
              onClick={goToAbout}
              className='py-2 text-start text-sm leading-5 text-muted-foreground cursor-pointer'
            >
              {tAbout('aboutUs')}
            </button>
            <LanguageToggle />
          </div>

          {hasLegalInfo && legalLinks.length > 0 && (
            <div className='flex flex-col gap-1.5'>
              {legalLinks.map((link) => (
                <button
                  key={link.label}
                  type='button'
                  onClick={() => {
                    handleNavigate({
                      fileName: link.label,
                      id: link.document?.id ?? '',
                      url: link.document?.url ?? '',
                      createdAt: link.document?.createdAt ?? '',
                    });
                    close();
                  }}
                  className='text-start text-xs leading-4 font-normal text-muted-foreground cursor-pointer'
                >
                  {link.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
